// One visit, everything it holds, and the SOAP note that is being typed into it
// right now. core.md §6, §7.5, §15.
//
// ── Three facts, loaded together, kept apart ──────────────────────────────
//
//   `encounter`  the visit itself with its note / dx / rx / orders — the thing
//                the کار column edits.
//   `record`     the whole patient record (`myCaseRecord`) — what the پرونده
//                rail READS: allergies, problems, current medications, history.
//                It is a separate query because the rail is read-only and must
//                not be repainted by every keystroke in the note below it.
//
// A visit whose `caseId` disagrees with the URL is NOT FOUND, not an error and
// not a redirect: the store answers `GetEncounter(encounter_id, student_id)` and
// the client compares (core.md §7.5). Anything else would let a student learn
// that a visit id exists by watching which of two wrong URLs behaves differently.
//
// ── The autosave, which is the only thing here with a memory ──────────────
//
// A SOAP note is prose typed for minutes at a time, and the student never presses
// «ذخیره». So: 800 ms after the last keystroke the note is upserted with ONLY the
// fields that changed against what the server last confirmed. Only-changed
// matters twice — it makes two tabs on one visit a last-write-wins per FIELD
// rather than per note (§7.5), and it makes a failed save retryable without
// re-sending three fields nobody touched.
//
// A failed save NEVER clears the textarea. The text stays, the indicator goes
// red, and the next keystroke (or a click on the indicator) retries. Meanwhile
// every change is mirrored into `localStorage.visit_draft_<encounterId>` with the
// wall-clock it was made at, so an expired session, a crashed tab or a closed
// laptop cannot take the paragraph with it.
//
// The ONE exception is a save the store refuses on principle: `ENCOUNTER_CLOSED`
// (the visit was closed in another tab) and `ENCOUNTER_NOT_FOUND` (it was
// cancelled there). Retrying those on every keystroke is a 409 per character
// that can never succeed, so instead the draft is DISCARDED, the encounter is
// re-read — which flips the screen to read-only with «بازگشایی» — and the
// student is told through `errors.encounterClosed` (core.md §6, §14).
//
// On the next load the draft and the server's `updatedAtMs` are compared:
// LOCAL NEWER → the draft is pushed up (the student's typing outlived whatever
// the server has); otherwise the draft is DISCARDED and the student is told,
// because keeping it would silently resurrect an older paragraph over a newer one.

import React from 'react';

import { bffQuery, bffMutation, errorCode, ERROR_CODES } from '../lib/bffClient.js';
import { readJson, writeJson, clearDraft, visitDraftKey } from '../lib/drafts.js';
import {
  ENCOUNTER_QUERY,
  MY_CASE_RECORD_QUERY,
  UPSERT_ENCOUNTER_NOTE_MUTATION,
  END_ENCOUNTER_MUTATION,
  REOPEN_ENCOUNTER_MUTATION,
  CANCEL_ENCOUNTER_MUTATION,
  ADD_DIAGNOSIS_MUTATION,
  UPDATE_DIAGNOSIS_MUTATION,
  DELETE_DIAGNOSIS_MUTATION,
  ADD_RX_ITEM_MUTATION,
  UPDATE_RX_ITEM_MUTATION,
  DELETE_RX_ITEM_MUTATION,
  ADD_ORDER_MUTATION,
  UPDATE_ORDER_MUTATION,
  RECORD_ORDER_RESULT_MUTATION,
  CANCEL_ORDER_MUTATION,
  DELETE_ORDER_MUTATION,
  ADD_CASE_HISTORY_MUTATION,
} from '../graphql/documents.js';

export const SOAP_FIELDS = ['s', 'o', 'a', 'p'];
export const AUTOSAVE_DELAY_MS = 800;

/** The `write` option for a mutation whose result the rail's derived views depend on. */
const DERIVES = Object.freeze({ derives: true });

/** The autosave indicator's three states, plus the one before anything happened. */
export const SaveState = Object.freeze({
  IDLE: 'idle',
  SAVING: 'saving',
  SAVED: 'saved',
  ERROR: 'error',
});

const emptySoap = () => ({ s: '', o: '', a: '', p: '' });

const soapFromNote = (note) => ({
  s: note?.s || '',
  o: note?.o || '',
  a: note?.a || '',
  p: note?.p || '',
});

/** The fields of `next` that differ from `base` — the whole upsert input. */
export function changedFields(base, next) {
  const out = {};
  for (const f of SOAP_FIELDS) if ((next?.[f] || '') !== (base?.[f] || '')) out[f] = next?.[f] || '';
  return out;
}

/**
 * What to do with a stored draft, given what the server holds. core.md §7.5.
 * Pure so the merge rule is testable without a network or a clock.
 *
 * `keep`    the draft is newer than the note — push it up.
 * `discard` the server moved on (another tab, another device) — drop it and say so.
 * `none`    nothing stored, or the draft says the same thing the server does.
 */
export function reconcileDraft(draft, note) {
  if (!draft || typeof draft !== 'object') return { action: 'none' };
  const soap = soapFromNote(draft);
  if (!SOAP_FIELDS.some((f) => soap[f])) return { action: 'none' };
  const serverSoap = soapFromNote(note);
  if (SOAP_FIELDS.every((f) => soap[f] === serverSoap[f])) return { action: 'none' };
  const localAt = Number(draft.updatedAtMs) || 0;
  const serverAt = Number(note?.updatedAtMs) || 0;
  // A draft with no clock is treated as OLDER: the only way it has no stamp is
  // that something wrote it that this module did not, and guessing in favour of
  // unstamped text is how an older paragraph overwrites a newer one.
  if (localAt > serverAt) return { action: 'keep', soap };
  return { action: 'discard', soap };
}

/**
 * @param encounterId the visit in the URL
 * @param caseId      the patient in the URL — the visit must agree with it
 * @param onToast     optional `{ info, error }`; the hook never imports the kit's
 *                    toast itself so it stays mountable in a bare test
 */
export function useEncounter(encounterId, caseId, { onToast } = {}) {
  const [encounter, setEncounter] = React.useState(null);
  const [record, setRecord] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [notFound, setNotFound] = React.useState(false);
  const [nonce, setNonce] = React.useState(0);

  const [soap, setSoap] = React.useState(emptySoap);
  const [saveState, setSaveState] = React.useState(SaveState.IDLE);
  const [saveError, setSaveError] = React.useState(null);

  // What the server last CONFIRMED. The diff is taken against this, never
  // against the previous render's state.
  const savedRef = React.useRef(emptySoap());
  const soapRef = React.useRef(emptySoap());
  const timerRef = React.useRef(null);
  const inFlightRef = React.useRef(null);
  const draftKey = encounterId ? visitDraftKey(encounterId) : null;
  const toastRef = React.useRef(onToast);
  toastRef.current = onToast;

  const isClosed = encounter?.status === 'closed';

  // ── Load ────────────────────────────────────────────────────────────────
  React.useEffect(() => {
    if (!encounterId) { setLoading(false); return undefined; }
    let alive = true;
    setLoading(true);
    setNotFound(false);

    bffQuery(ENCOUNTER_QUERY, { encounterId })
      .then(async (data) => {
        if (!alive) return;
        const row = data?.encounter || null;
        // null = not found AND not mine, deliberately the same screen; a visit
        // filed under another patient is the same answer for the same reason.
        if (!row || (caseId && row.caseId && row.caseId !== caseId)) {
          setNotFound(true);
          setEncounter(null);
          setError(null);
          return;
        }
        setEncounter(row);
        setError(null);

        const serverSoap = soapFromNote(row.note);
        const stored = draftKey ? readJson(draftKey, { kind: 'local', fallback: null }) : null;
        const verdict = reconcileDraft(stored, row.note);
        if (verdict.action === 'keep') {
          savedRef.current = serverSoap;
          soapRef.current = verdict.soap;
          setSoap(verdict.soap);
          // Push it now rather than waiting for a keystroke: the student may
          // never touch the note again, and the whole point of the draft is
          // that it stops being local.
          scheduleSave(0);
        } else {
          if (verdict.action === 'discard') {
            clearDraft(draftKey, { kind: 'local' });
            toastRef.current?.info?.('discardedDraft');
          }
          savedRef.current = serverSoap;
          soapRef.current = serverSoap;
          setSoap(serverSoap);
          setSaveState(SaveState.IDLE);
        }

        // The rail's data. A failure here leaves the workspace usable: the note
        // is the thing being written, and a rail that could not load is a rail
        // that renders empty, not a screen that refuses to open.
        try {
          const rec = await bffQuery(MY_CASE_RECORD_QUERY, { caseId: caseId || row.caseId });
          if (alive) setRecord(rec?.myCaseRecord || null);
        } catch (err) {
          console.warn('[useEncounter] record load failed:', errorCode(err) || err?.message);
        }
      })
      .catch((err) => {
        if (!alive) return;
        console.error('[useEncounter] failed:', errorCode(err) || err?.message);
        setError(err);
      })
      .finally(() => { if (alive) setLoading(false); });

    return () => { alive = false; };
    // `scheduleSave` is stable-by-ref below; listing it would re-run the load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [encounterId, caseId, nonce]);

  const reload = React.useCallback(() => setNonce((n) => n + 1), []);

  // ── The save itself ─────────────────────────────────────────────────────

  const runSave = React.useCallback(async () => {
    if (!encounterId) return { ok: true };
    const next = soapRef.current;
    const input = changedFields(savedRef.current, next);
    if (!Object.keys(input).length) return { ok: true };
    // A closed visit refuses every clinical write; sending it would be a 409 we
    // already know the answer to.
    if (encounter?.status === 'closed') return { ok: true };

    setSaveState(SaveState.SAVING);
    const attempt = { ...next };
    const promise = bffMutation(UPSERT_ENCOUNTER_NOTE_MUTATION, { encounterId, input })
      .then((data) => {
        savedRef.current = attempt;
        setEncounter((prev) => (prev ? { ...prev, note: data?.upsertEncounterNote || prev.note } : prev));
        // Only once the store holds it does the local copy stop being the truth.
        if (draftKey) clearDraft(draftKey, { kind: 'local' });
        setSaveError(null);
        setSaveState(SaveState.SAVED);
        return { ok: true };
      })
      .catch((err) => {
        console.warn('[useEncounter] note save failed:', errorCode(err) || err?.message);
        if (isTerminalSaveError(err)) {
          // Not retryable: fall back to what the server last confirmed, drop
          // the mirror, and let the reload say what the visit has become.
          if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
          soapRef.current = { ...savedRef.current };
          setSoap(soapRef.current);
          if (draftKey) clearDraft(draftKey, { kind: 'local' });
          setSaveError(null);
          setSaveState(SaveState.IDLE);
          toastRef.current?.error?.('encounterClosed');
          setNonce((n) => n + 1);
          return { ok: false, error: err };
        }
        setSaveError(err);
        setSaveState(SaveState.ERROR);
        return { ok: false, error: err };
      })
      .finally(() => { inFlightRef.current = null; });

    inFlightRef.current = promise;
    return promise;
  }, [encounterId, draftKey, encounter?.status]);

  const runSaveRef = React.useRef(runSave);
  runSaveRef.current = runSave;

  const scheduleSave = React.useCallback((delay = AUTOSAVE_DELAY_MS) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      runSaveRef.current();
    }, delay);
  }, []);

  React.useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  /** Type into one SOAP field. Debounces the save and mirrors the draft. */
  const setSoapField = React.useCallback((field, value) => {
    if (!SOAP_FIELDS.includes(field)) return;
    const next = { ...soapRef.current, [field]: value ?? '' };
    soapRef.current = next;
    setSoap(next);
    if (draftKey) writeJson(draftKey, { ...next, updatedAtMs: Date.now() }, { kind: 'local' });
    scheduleSave();
  }, [draftKey, scheduleSave]);

  /**
   * Apply a whole suggestion — one section or four — as if it had been typed.
   * `mode` is the chooser the review sheet offers: replace the section, or add
   * the draft under what is already there.
   */
  const applySoap = React.useCallback((patch, mode = 'replace') => {
    // A closed visit takes no suggestion: the review sheet is gated on the same
    // fact upstream, and this is the guard for a sheet that was already open
    // when the visit closed under it.
    if (encounter?.status === 'closed') return;
    const next = { ...soapRef.current };
    for (const f of SOAP_FIELDS) {
      if (patch?.[f] == null) continue;
      const incoming = String(patch[f]);
      next[f] = mode === 'append' && next[f]
        ? `${next[f]}\n${incoming}`
        : incoming;
    }
    soapRef.current = next;
    setSoap(next);
    if (draftKey) writeJson(draftKey, { ...next, updatedAtMs: Date.now() }, { kind: 'local' });
    scheduleSave();
  }, [draftKey, scheduleSave, encounter?.status]);

  /** Save NOW. The closure dialog awaits this before it ends the visit. */
  const flush = React.useCallback(async () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (inFlightRef.current) await inFlightRef.current;
    return runSaveRef.current();
  }, []);

  /** The indicator is clickable when it is red — that click is this. */
  const retrySave = React.useCallback(() => flush(), [flush]);

  // ── Clinical writes ─────────────────────────────────────────────────────
  //
  // Every one of them patches the encounter in place rather than re-reading it:
  // the mutation answers with the row it wrote, and a refetch would repaint the
  // three columns around a student who is mid-sentence.

  const patchList = React.useCallback((key, fn) => {
    setEncounter((prev) => (prev ? { ...prev, [key]: fn(prev[key] || []) } : prev));
  }, []);

  // The rail's DERIVED views («مشکلات فعال», «داروهای فعلی») are computed by the
  // BFF from every visit's diagnoses and prescriptions, so a dx/rx write in THIS
  // visit changes them and a patched encounter cannot know how. The record is
  // re-read after those writes — and only those: the note, orders and vitals
  // feed nothing the rail derives. Best-effort, and never on the write's own
  // promise: a rail that failed to refresh is not a diagnosis that failed to
  // save.
  const refreshRecord = React.useCallback(async () => {
    const targetCase = caseId || encounter?.caseId;
    if (!targetCase) return;
    try {
      const rec = await bffQuery(MY_CASE_RECORD_QUERY, { caseId: targetCase });
      if (rec?.myCaseRecord) setRecord(rec.myCaseRecord);
    } catch (err) {
      console.warn('[useEncounter] record refresh failed:', errorCode(err) || err?.message);
    }
  }, [caseId, encounter?.caseId]);

  const write = React.useCallback(async (doc, vars, apply, { derives = false } = {}) => {
    const data = await bffMutation(doc, vars);
    apply?.(data);
    if (derives) refreshRecord();
    return data;
  }, [refreshRecord]);

  const addDiagnosis = React.useCallback((input) => write(
    ADD_DIAGNOSIS_MUTATION, { encounterId, input },
    (d) => d?.addDiagnosis && patchList('diagnoses', (rows) => [...rows, d.addDiagnosis]),
    DERIVES,
  ), [encounterId, patchList, write]);

  const updateDiagnosis = React.useCallback((diagnosisId, input) => write(
    UPDATE_DIAGNOSIS_MUTATION, { diagnosisId, input },
    (d) => d?.updateDiagnosis && patchList('diagnoses', (rows) =>
      rows.map((r) => (r.id === diagnosisId ? d.updateDiagnosis : r))),
    DERIVES,
  ), [patchList, write]);

  const deleteDiagnosis = React.useCallback((diagnosisId) => write(
    DELETE_DIAGNOSIS_MUTATION, { diagnosisId },
    () => patchList('diagnoses', (rows) => rows.filter((r) => r.id !== diagnosisId)),
    DERIVES,
  ), [patchList, write]);

  /**
   * Drag-reorder. `sortOrder` is a column on the row, so the order is persisted
   * one row at a time — and the list is repainted optimistically first, because
   * a drag that snaps back while four mutations fly is not a drag.
   */
  const reorderDiagnoses = React.useCallback(async (ids) => {
    patchList('diagnoses', (rows) => ids
      .map((id, i) => {
        const row = rows.find((r) => r.id === id);
        return row ? { ...row, sortOrder: i } : null;
      })
      .filter(Boolean));
    for (let i = 0; i < ids.length; i += 1) {
      // Sequential on purpose: they all touch one list on one row's student, and
      // a parallel burst is a burst the store answers out of order.
      // eslint-disable-next-line no-await-in-loop
      await bffMutation(UPDATE_DIAGNOSIS_MUTATION, { diagnosisId: ids[i], input: { sortOrder: i } })
        .catch((err) => console.warn('[useEncounter] reorder failed:', errorCode(err) || err?.message));
    }
  }, [patchList]);

  const addRxItem = React.useCallback((input) => write(
    ADD_RX_ITEM_MUTATION, { encounterId, input },
    (d) => d?.addRxItem && patchList('rxItems', (rows) => [...rows, d.addRxItem]),
    DERIVES,
  ), [encounterId, patchList, write]);

  const updateRxItem = React.useCallback((rxItemId, input) => write(
    UPDATE_RX_ITEM_MUTATION, { rxItemId, input },
    (d) => d?.updateRxItem && patchList('rxItems', (rows) =>
      rows.map((r) => (r.id === rxItemId ? d.updateRxItem : r))),
    DERIVES,
  ), [patchList, write]);

  const deleteRxItem = React.useCallback((rxItemId) => write(
    DELETE_RX_ITEM_MUTATION, { rxItemId },
    () => patchList('rxItems', (rows) => rows.filter((r) => r.id !== rxItemId)),
    DERIVES,
  ), [patchList, write]);

  const addOrder = React.useCallback((input) => write(
    ADD_ORDER_MUTATION, { encounterId, input },
    (d) => d?.addOrder && patchList('orders', (rows) => [...rows, d.addOrder]),
  ), [encounterId, patchList, write]);

  const updateOrder = React.useCallback((orderId, input) => write(
    UPDATE_ORDER_MUTATION, { orderId, input },
    (d) => d?.updateOrder && patchList('orders', (rows) =>
      rows.map((r) => (r.id === orderId ? d.updateOrder : r))),
  ), [patchList, write]);

  const recordOrderResult = React.useCallback((orderId, input) => write(
    RECORD_ORDER_RESULT_MUTATION, { orderId, input },
    (d) => d?.recordOrderResult && patchList('orders', (rows) =>
      rows.map((r) => (r.id === orderId ? d.recordOrderResult : r))),
  ), [patchList, write]);

  const cancelOrder = React.useCallback((orderId) => write(
    CANCEL_ORDER_MUTATION, { orderId },
    (d) => d?.cancelOrder && patchList('orders', (rows) =>
      rows.map((r) => (r.id === orderId ? d.cancelOrder : r))),
  ), [patchList, write]);

  const deleteOrder = React.useCallback((orderId) => write(
    DELETE_ORDER_MUTATION, { orderId },
    () => patchList('orders', (rows) => rows.filter((r) => r.id !== orderId)),
  ), [patchList, write]);

  /**
   * The rail's «ثبت» quick-add: a `vitals` history entry STAMPED with this
   * visit, so the timeline groups it under «ویزیت N» rather than «خارج از ویزیت»
   * (core.md §7.5). It writes to the case, not to the encounter — a measurement
   * survives the visit being cancelled (§5.1).
   */
  const addVitals = React.useCallback(async ({ body, structured, label }) => {
    const targetCase = caseId || encounter?.caseId;
    if (!targetCase) return null;
    const data = await bffMutation(ADD_CASE_HISTORY_MUTATION, {
      caseId: targetCase,
      entries: [{
        section: 'vitals',
        label: label || '',
        body: body || '',
        structured: structured || null,
        source: 'manual',
        encounterId,
      }],
    });
    const rows = data?.addCaseHistory || [];
    if (rows.length) {
      setRecord((prev) => (prev?.case
        ? { ...prev, case: { ...prev.case, entries: [...(prev.case.entries || []), ...rows] } }
        : prev));
      patchList('entries', (existing) => [...existing, ...rows]);
    }
    return rows;
  }, [caseId, encounter?.caseId, encounterId, patchList]);

  // ── Lifecycle ───────────────────────────────────────────────────────────

  const endEncounter = React.useCallback(async (summary) => {
    const data = await bffMutation(END_ENCOUNTER_MUTATION, { encounterId, summary: summary || null });
    const row = data?.endEncounter;
    if (row) setEncounter((prev) => ({ ...(prev || {}), ...row }));
    if (draftKey) clearDraft(draftKey, { kind: 'local' });
    return row;
  }, [encounterId, draftKey]);

  const reopenEncounter = React.useCallback(async () => {
    const data = await bffMutation(REOPEN_ENCOUNTER_MUTATION, { encounterId });
    const row = data?.reopenEncounter;
    if (row) setEncounter((prev) => ({ ...(prev || {}), ...row }));
    return row;
  }, [encounterId]);

  const cancelEncounter = React.useCallback(async () => {
    // Whatever is in the note is about to stop existing; a draft that outlived
    // it would be replayed into the NEXT visit's editor.
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (draftKey) clearDraft(draftKey, { kind: 'local' });
    return bffMutation(CANCEL_ENCOUNTER_MUTATION, { encounterId });
  }, [encounterId, draftKey]);

  return {
    encounter,
    record,
    loading,
    error,
    notFound,
    isClosed,
    reload,
    refreshRecord,

    soap,
    setSoapField,
    applySoap,
    saveState,
    saveError,
    flush,
    retrySave,
    hasUnsaved: Object.keys(changedFields(savedRef.current, soap)).length > 0,

    addDiagnosis,
    updateDiagnosis,
    deleteDiagnosis,
    reorderDiagnoses,
    addRxItem,
    updateRxItem,
    deleteRxItem,
    addOrder,
    updateOrder,
    recordOrderResult,
    cancelOrder,
    deleteOrder,
    addVitals,

    endEncounter,
    reopenEncounter,
    cancelEncounter,
  };
}

/** True when a write failed because the visit is closed (core.md §14). */
export const isEncounterClosedError = (err) =>
  errorCode(err) === ERROR_CODES.ENCOUNTER_CLOSED;

/**
 * A save the store will refuse however many times it is retried: the visit is
 * closed, or no longer exists. Anything else (a downstream outage, the network)
 * is retried on the next keystroke.
 */
export const isTerminalSaveError = (err) => {
  const code = errorCode(err);
  return !!code && (code === ERROR_CODES.ENCOUNTER_CLOSED || code === ERROR_CODES.ENCOUNTER_NOT_FOUND);
};

export default useEncounter;
